/**
 * Certificate Viewer Component
 * Modal to view medical certificates (PDF/images)
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { MedicalLeave, CertificateInfo } from '../../../types/medicalLeave';
import { medicalLeaveService } from '../../../services/medicalLeaveService';

interface CertificateViewerProps {
  open: boolean;
  onClose: () => void;
  medicalLeave: MedicalLeave | null;
}

export const CertificateViewer: React.FC<CertificateViewerProps> = ({
  open,
  onClose,
  medicalLeave,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [certificateInfo, setCertificateInfo] = useState<CertificateInfo | null>(null);

  useEffect(() => {
    if (open && medicalLeave?.id && medicalLeave.hasCertificate) {
      loadCertificate();
    }
  }, [open, medicalLeave]);

  const loadCertificate = async () => {
    if (!medicalLeave?.id) return;

    try {
      setLoading(true);
      setError(null);
      const info = await medicalLeaveService.getCertificate(medicalLeave.id);
      setCertificateInfo(info);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar atestado');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!medicalLeave?.id) return;
    const url = medicalLeaveService.getCertificateDownloadUrl(medicalLeave.id);
    window.open(url, '_blank');
  };

  const handleOpenInNewTab = () => {
    if (!medicalLeave?.id) return;
    const url = medicalLeaveService.getCertificateDownloadUrl(medicalLeave.id);
    window.open(url, '_blank');
  };

  const isImage = certificateInfo?.fileExtension &&
    ['jpg', 'jpeg', 'png', 'gif'].includes(certificateInfo.fileExtension.toLowerCase());

  const isPdf = certificateInfo?.fileExtension?.toLowerCase() === 'pdf';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Atestado Médico - {medicalLeave?.employeeName}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && !medicalLeave?.hasCertificate && (
          <Alert severity="info">
            Esta licença médica não possui atestado anexado.
          </Alert>
        )}

        {!loading && !error && certificateInfo && medicalLeave && (
          <Box sx={{ minHeight: 400 }}>
            {isImage && (
              <Box sx={{ textAlign: 'center' }}>
                <img
                  src={medicalLeaveService.getCertificateDownloadUrl(medicalLeave.id)}
                  alt="Atestado Médico"
                  style={{ maxWidth: '100%', maxHeight: '70vh' }}
                />
              </Box>
            )}

            {isPdf && (
              <Box sx={{ height: '70vh', width: '100%' }}>
                <iframe
                  src={medicalLeaveService.getCertificateDownloadUrl(medicalLeave.id)}
                  title="Atestado Médico"
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                />
              </Box>
            )}

            {!isImage && !isPdf && (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="body1" gutterBottom>
                  Arquivo: {certificateInfo.fileName}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Este tipo de arquivo não pode ser visualizado diretamente.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  sx={{ mt: 2 }}
                >
                  Baixar Arquivo
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {certificateInfo && medicalLeave && (
          <>
            <Button
              startIcon={<OpenInNewIcon />}
              onClick={handleOpenInNewTab}
            >
              Abrir em Nova Aba
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Download
            </Button>
          </>
        )}
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CertificateViewer;
